import { BillingMethodDTO } from 'billing/dtos/billing-method'
import { Model } from 'infra/model'
import { once } from 'lodash'
import { computed, observable, makeObservable } from 'mobx'
import { Id } from 'types/definitions'
import BillingProfile from './BillingProfile'

export default class BillingMethod extends Model<
  BillingMethodDTO,
  BillingMethod
> {
  id = Id.none
  type: BillingMethodDTO['type'] = 'Credit'

  @observable balance = 0
  @observable limit = 0
  @observable expMonth = 0
  @observable expYear = 0
  @observable brand = ''
  @observable last4 = ''
  @observable workspaceId = Id.none

  /**
   * Delete the Billing Method.
   */
  delete = () => this.onDelete(this)

  constructor(
    public billingProfile: BillingProfile,
    private onDelete: (method: BillingMethod) => Promise<unknown>,
  ) {
    super()
    makeObservable(this)
  }

  static get none() {
    return lazyNone()
  }

  @computed
  get default() {
    return this.billingProfile.defaultBillingMethodId === this.id
  }

  /**
   * Whether the current user is allowed to delete this billing method.
   */
  @computed
  get allowedToDelete() {
    return this.billingProfile.allowedToManage
  }

  @computed
  get remainingBalance() {
    return this.balance + this.limit
  }

  parse(json: BillingMethodDTO): Partial<BillingMethod> {
    return {
      id: json.id,
      type: json.type,
      workspaceId: json.workspace_id,
      ...(json.type === 'Stripe' && {
        brand: json.brand,
        last4: json.last4,
        expMonth: json.exp_month,
        expYear: json.exp_year,
      }),
      ...(json.type === 'StripeCardPaymentMethod' && {
        brand: json.brand,
        last4: json.last4,
        expMonth: json.exp_month,
        expYear: json.exp_year,
      }),
      ...(json.type === 'Credit' && {
        balance: json.balance,
        limit: json.limit,
      }),
    }
  }
}

const lazyNone = once(
  () => new BillingMethod(BillingProfile.none, () => Promise.resolve()),
)
